// lib/project-layer-preview.ts
import { getLayerWorkspaceId, type Layer } from '#types/types';

export const MAX_PROJECT_PREVIEW_HYDRATION = 12;
export const MAX_PROJECT_PREVIEW_CACHE_BYTES = 2 * 1024 * 1024 * 1024;

// Tile observers share their Kernel's in-flight set, so singleton requests use one limit too.
const hydrationSlots = new WeakMap<Set<string>, { active: number; waiting: Array<() => void> }>();

const acquireHydrationSlot = async (inflight: Set<string>): Promise<() => void> => {
  let slots = hydrationSlots.get(inflight);
  if (!slots) {
    slots = { active: 0, waiting: [] };
    hydrationSlots.set(inflight, slots);
  }
  if (slots.active >= MAX_PROJECT_PREVIEW_HYDRATION) {
    await new Promise<void>(resolve => slots.waiting.push(resolve));
  } else {
    slots.active += 1;
  }
  return () => {
    const next = slots.waiting.shift();
    if (next) next();
    else slots.active -= 1;
  };
};

const imageDataByteLength = (imageData: ImageData): number => {
  const byteLength = imageData.data?.byteLength;
  return typeof byteLength === 'number' && Number.isFinite(byteLength)
    ? byteLength
    : Math.max(1, imageData.width * imageData.height * 4);
};

/** Hydrates detached raster layers with a bounded runtime pixel cache. */
export const hydrateProjectLayerPreviews = async ({
  projectId,
  layers,
  requestedLayerIds,
  retry = false,
  inflightLayerIds,
  cacheEntries,
  deferredLayerSources = new Map<string, string>(),
  loadPixels,
  setLayer,
  reportAssetLoaded,
}: {
  projectId: string;
  layers: readonly Layer[];
  requestedLayerIds?: ReadonlySet<string> | null;
  retry?: boolean;
  inflightLayerIds: Set<string>;
  cacheEntries: Map<string, { bytes: number; lastUsed: number }>;
  /** Layers that could not fit stay placeholders until an explicit retry. */
  deferredLayerSources?: Map<string, string>;
  loadPixels: (source: string) => Promise<ImageData>;
  setLayer: (layerId: string, updater: (layer: Layer) => Layer) => void;
  reportAssetLoaded: (source: string) => void;
}): Promise<void> => {
  const allProjectRasterLayers = layers.filter((layer) => (
    getLayerWorkspaceId(layer) === null && !layer.type
  ));
  const projectRasterLayers = allProjectRasterLayers.filter((layer) => layer.projectId?.trim() === projectId);
  const layersById = new Map(allProjectRasterLayers.map((layer) => [layer.id, layer]));
  for (const layer of allProjectRasterLayers) {
    if (!layer.imageData) continue;
    const previous = cacheEntries.get(layer.id);
    const wasRequested = requestedLayerIds === undefined || requestedLayerIds === null || requestedLayerIds.has(layer.id);
    cacheEntries.set(layer.id, {
      bytes: imageDataByteLength(layer.imageData),
      lastUsed: wasRequested || !previous ? Date.now() : previous.lastUsed,
    });
  }
  for (const layerId of cacheEntries.keys()) {
    if (!layersById.get(layerId)?.imageData) cacheEntries.delete(layerId);
  }
  for (const [layerId, source] of deferredLayerSources.entries()) {
    const layer = layersById.get(layerId);
    if (!layer || layer.imageData || layer.imageSrc !== source) deferredLayerSources.delete(layerId);
  }
  if (retry) {
    for (const layerId of requestedLayerIds ?? projectRasterLayers.map((layer) => layer.id)) {
      deferredLayerSources.delete(layerId);
    }
  }

  const candidates = projectRasterLayers.filter((layer) => {
    if (requestedLayerIds && !requestedLayerIds.has(layer.id)) return false;
    if (!layer.imageSrc || layer.imageData || (layer.loadError && !retry)) return false;
    if (deferredLayerSources.has(layer.id) && !retry) return false;
    if (inflightLayerIds.has(layer.id)) return false;
    inflightLayerIds.add(layer.id);
    return true;
  });

  const reservePreviewCache = (incomingBytes: number): boolean => {
    const currentBytes = Array.from(cacheEntries.values()).reduce((total, entry) => total + entry.bytes, 0);
    return currentBytes + incomingBytes <= MAX_PROJECT_PREVIEW_CACHE_BYTES;
  };

  let nextCandidateIndex = 0;
  const workerCount = Math.min(MAX_PROJECT_PREVIEW_HYDRATION, candidates.length);
  const workers = Array.from({ length: workerCount }, async () => {
    while (true) {
      const layer = candidates[nextCandidateIndex++];
      if (!layer) return;
      const source = layer.imageSrc;
      if (!source) {
        inflightLayerIds.delete(layer.id);
        continue;
      }
      const releaseSlot = await acquireHydrationSlot(inflightLayerIds);
      try {
        const imageData = await loadPixels(source);
        if (!reservePreviewCache(imageDataByteLength(imageData))) {
          let deferred = false;
          setLayer(layer.id, (targetLayer) => {
            if (targetLayer.projectId?.trim() !== projectId || getLayerWorkspaceId(targetLayer) !== null || targetLayer.imageSrc !== source) {
              return targetLayer;
            }
            deferred = true;
            return { ...targetLayer, loadError: true, mustRender: false };
          });
          if (deferred) deferredLayerSources.set(layer.id, source);
          continue;
        }
        let applied = false;
        setLayer(layer.id, (targetLayer) => {
          if (targetLayer.projectId?.trim() !== projectId || getLayerWorkspaceId(targetLayer) !== null || targetLayer.imageSrc !== source) {
            return targetLayer;
          }
          applied = true;
          return { ...targetLayer, imageData, isAltered: false, mustRender: false, loadError: false };
        });
        if (applied) {
          cacheEntries.set(layer.id, {
            bytes: imageDataByteLength(imageData),
            lastUsed: Date.now(),
          });
        }
      } catch {
        setLayer(layer.id, (targetLayer) => {
          if (targetLayer.projectId?.trim() !== projectId || getLayerWorkspaceId(targetLayer) !== null || targetLayer.imageSrc !== source) {
            return targetLayer;
          }
          return { ...targetLayer, loadError: true, mustRender: false };
        });
      } finally {
        releaseSlot();
        inflightLayerIds.delete(layer.id);
        reportAssetLoaded(source);
      }
    }
  });
  await Promise.all(workers);
};
